import React, { useEffect, useState } from "react";
import { IconAddPhotoAlternate } from "assets/svg";
import {
  Button,
  Input,
  message,
  Radio,
  Select,
  Upload,
  Image,
  UploadFile,
  UploadProps,
  InputNumber,
} from "antd";
import {
  DISPOSAL_TYPE,
  PRODUCT_CATEGORY,
  PRODUCT_STATE,
  SHIP_CHARGE_TYPE,
  SHIP_DAY,
} from "common/constants";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { uploadCleanData, uploadImageMultiple } from "reducers/uploadSlice";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "store/store";
import { useCommonContext } from "contexts/CommonContext";
import { createProduct, productCleanData } from "reducers/productSlice";
import { FileData, FileType, ProductInfo } from "common/interfaces";
import { getBase64 } from "common/utils";
import { isEmpty } from "lodash";

const style: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 8,
};

const CreateProduct = () => {
  const { setHeaderTitle } = useCommonContext();
  const dispatch = useDispatch<AppDispatch>();
  const { uploadImageMultipleData, error: uploadErr } = useSelector(
    (state: RootState) => state.upload,
  );
  const { createProductData, error: productErr } = useSelector(
    (state: RootState) => state.product,
  );
  const { TextArea } = Input;
  const controller = new AbortController();
  const navigate = useNavigate();
  const defaultValues: ProductInfo = {
    product_name: "",
    category_id: undefined,
    quantity_value: undefined,
    disposal_type: "0",
    state: undefined,
    description: "",
    shipping_charge_type: undefined,
    shipping_days: undefined,
    is_free: false,
    price: undefined,
    status: 1,
    image_urls: [],
  };
  const { handleSubmit, control, watch, reset } = useForm<ProductInfo>({
    defaultValues,
  });
  const {
    productInfoContext,
    setProductInfoContext,
    fileList,
    setFileList,
    clearFileList,
  } = useCommonContext();
  const [isSubmit, setIsSubmit] = useState<boolean>(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState("");
  const [hasError, setHasError] = useState(false);
  const MAX_FILES = 5;
  const MAX_TOTAL_SIZE = 50 * 1024 * 1024; // 50MB

  const productName: string = watch("product_name");
  const categoryId = watch("category_id");
  const disposalType: string = watch("disposal_type");
  const quantityValue = watch("quantity_value");
  const state = watch("state");
  const description: string = watch("description");
  const shippingChargeType = watch("shipping_charge_type");
  const shippingDays = watch("shipping_days");
  const isFree: boolean = watch("is_free");
  const price = watch("price");

  const productInfo: ProductInfo = {
    product_name: productName,
    category_id: categoryId,
    quantity_value: quantityValue,
    disposal_type: disposalType,
    state: state,
    description: description,
    shipping_charge_type: shippingChargeType,
    shipping_days: shippingDays,
    is_free: isFree,
    price: !isFree ? price : undefined,
    status: 1,
    image_urls:
      (!isEmpty(uploadImageMultipleData) &&
        uploadImageMultipleData?.data.map((i: FileData) => i.path)) ||
      [],
  };

  const handlePreview = async (file: UploadFile) => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj as FileType);
    }
    setPreviewImage(file.url || (file.preview as string));
    setPreviewOpen(true);
  };

  const handleChange: UploadProps["onChange"] = ({ fileList: newFileList }) =>
    setFileList(newFileList);

  const beforeUpload = (file: File, fileListInEvent: File[]) => {
    if (!file.type.startsWith("image/")) {
      message.error(`${file.name} 有効な画像ファイルではありません`);
      return Upload.LIST_IGNORE;
    }
    const currentTotalSize = (fileList ?? []).reduce(
      (sum, f) =>
        sum + (f.originFileObj instanceof File ? f.originFileObj.size : 0),
      0,
    );
    const newFilesTotalSize = fileListInEvent.reduce(
      (sum, f) => sum + f.size,
      0,
    );
    const totalSize = currentTotalSize + newFilesTotalSize;
    if (totalSize > MAX_TOTAL_SIZE) {
      message.error("写真の合計サイズが10MBを超えています");
      return Upload.LIST_IGNORE;
    }
    if ((fileList?.length ?? 0) + fileListInEvent.length > MAX_FILES) {
      message.error(`アップロードできるのは ${MAX_FILES} 画像`);
      return Upload.LIST_IGNORE;
    }
    return false;
  };

  const onDraft = () => {
    const formData = new FormData();
    fileList?.forEach((file: any) => {
      const actualFile = file.originFileObj;
      if (actualFile) {
        formData.append("files", actualFile);
      }
    });
    if (!isEmpty(fileList)) {
      dispatch(uploadImageMultiple(formData, "product", controller.signal));
    }
  };

  const onSubmit = () => {
    const formData = new FormData();
    fileList?.forEach((file: any) => {
      const actualFile = file.originFileObj;
      if (actualFile) {
        formData.append("files", actualFile);
      }
    });
    if (!isEmpty(fileList)) {
      setHasError(false);
      dispatch(uploadImageMultiple(formData, "product", controller.signal));
      setIsSubmit(true);
    } else {
      setHasError(true);
      message.error("写真を選択してください");
    }
  };

  const saveToSession = (productInfo: ProductInfo) => {
    sessionStorage.setItem("productInfo", JSON.stringify(productInfo));
  };

  useEffect(() => {
    if (isSubmit && uploadImageMultipleData?.success) {
      setProductInfoContext(productInfo);
      saveToSession(productInfo);
      navigate("/seller/product/check-list-detail");
    }
    if (!isSubmit && uploadImageMultipleData?.success) {
      dispatch(
        createProduct(
          {
            ...productInfo,
            status: 0,
          },
          controller.signal,
        ),
      );
    }
  }, [uploadImageMultipleData, isSubmit]);

  useEffect(() => {
    if (createProductData?.success) {
      message.success("製品の作成に成功しました");
      reset({});
      setFileList([]);
      setProductInfoContext(null);
      dispatch(productCleanData());
      dispatch(uploadCleanData());
      navigate("/seller/product/list");
    }
  }, [createProductData]);

  useEffect(() => {
    if (uploadErr) {
      message.error(uploadErr);
    }
  }, [uploadErr]);

  useEffect(() => {
    if (productErr) {
      message.error(productErr);
    }
  }, [productErr]);

  useEffect(() => {
    if (productInfoContext) {
      reset({
        product_name: productInfoContext.product_name,
        category_id: productInfoContext.category_id,
        quantity_value: productInfoContext.quantity_value,
        disposal_type: productInfoContext.disposal_type || "0",
        state: productInfoContext.state,
        description: productInfoContext.description,
        shipping_charge_type: productInfoContext.shipping_charge_type,
        shipping_days: productInfoContext.shipping_days,
        is_free: productInfoContext.is_free,
        price:
          (!productInfoContext.is_free && productInfoContext.price) ||
          undefined,
        status: 1,
        image_urls: productInfoContext.image_urls,
      });
    }
  }, [productInfoContext]);

  useEffect(() => {
    dispatch(uploadCleanData());
    dispatch(productCleanData());
    setHeaderTitle("商品の出品");
    return () => {
      const isCheckListDetail = location.pathname.includes(
        "/seller/product/check-list-detail",
      );
      if (!isCheckListDetail) {
        setProductInfoContext(null);
        setFileList([]);
        clearFileList();
        sessionStorage.removeItem("productInfo");
      }
      dispatch(uploadCleanData());
      dispatch(productCleanData());
    };
  }, []);

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="w-full">
      <div className="self-stretch w-full min-h-[924px] p-[20px] pb-[100px] md:p-10 bg-white inline-flex flex-col justify-start items-start gap-[30px] md:gap-12 overflow-hidden">
        <div className="self-stretch flex flex-col justify-start items-start gap-[30px] md:gap-10">
          <div className="self-stretch flex flex-col justify-start items-start gap-[20px] md:gap-7">
            <div
              data-プロパティ1="base + text"
              className="self-stretch py-2.5 border-b border-neutral-border inline-flex justify-start items-center gap-3.5"
            >
              <div className="w-1 self-stretch bg-primary rounded-sm" />
              <div className="flex-1 flex justify-start items-center gap-2.5">
                <div className="flex justify-start items-center gap-[5px]">
                  <div className="justify-start text-primary text-[18px] md:text-[22px] font-bold font-['Noto_Sans_JP'] leading-9">
                    出品画像
                  </div>
                  <div className="justify-start text-primary text-[14px] md:text-base font-normal font-['Noto_Sans_JP'] leading-relaxed">
                    （最大5枚）
                  </div>
                </div>
              </div>
            </div>
            {previewImage && (
              <Image
                wrapperStyle={{ display: "none" }}
                preview={{
                  visible: previewOpen,
                  onVisibleChange: (visible) => setPreviewOpen(visible),
                  afterOpenChange: (visible) => !visible && setPreviewImage(""),
                }}
                src={previewImage}
              />
            )}
            <Upload
              multiple
              listType="picture-card"
              fileList={fileList}
              onPreview={handlePreview}
              onChange={handleChange}
              beforeUpload={beforeUpload}
              className="w-full"
              style={{ width: "100%", height: "100%", border: "none" }}
            >
              <div className="w-full self-stretch bg-white flex flex-col justify-start items-start gap-3.5">
                <div
                  className={`self-stretch md:p-12 bg-slate-50 rounded-[6px] md:rounded-xl flex flex-col justify-start items-center gap-5 overflow-hidden md:border border-dashed ${
                    hasError ? "border-red-dark" : "border-primary"
                  }`}
                >
                  <div className="hidden self-stretch md:flex flex-col justify-start items-center gap-2.5">
                    <div className="self-stretch text-center justify-start text-primary text-xl font-bold font-['Noto_Sans_JP'] leading-loose">
                      写真をドラッグして追加
                    </div>
                    <div className="self-stretch text-center justify-start text-black text-base font-normal font-['Noto_Sans_JP'] leading-relaxed">
                      商品の全体、詳細、文字入りの写真ををアップロードしてください
                    </div>
                  </div>
                  <div className="hidden md:block self-stretch text-center justify-start text-black text-sm font-normal font-['Noto_Sans_JP'] leading-snug">
                    ー または ー
                  </div>
                  <div
                    data-プロパティ1="base + icon"
                    className="h-10 min-w-full md:min-w-24 px-5 bg-primary rounded-md inline-flex justify-center items-center gap-[5px] overflow-hidden"
                  >
                    <IconAddPhotoAlternate className="w-[20px] h-[20px] aspect-[1/1]" />
                    <div className="text-center justify-start text-white text-sm font-bold font-['Noto_Sans_JP'] leading-snug">
                      画像を選択する
                    </div>
                  </div>
                </div>
              </div>
            </Upload>
          </div>
          <div className="self-stretch flex flex-col justify-start items-start gap-[20px] md:gap-7">
            <div
              data-プロパティ1="base"
              className="self-stretch py-2.5 border-b border-neutral-border inline-flex justify-start items-center gap-3.5"
            >
              <div className="w-1 self-stretch bg-primary rounded-sm" />
              <div className="flex-1 flex justify-start items-center gap-2.5">
                <div className="flex justify-start items-center gap-[5px]">
                  <div className="justify-start text-primary text-[18px] md:text-[22px] font-bold font-['Noto_Sans_JP'] leading-9">
                    商品の基本情報
                  </div>
                </div>
              </div>
            </div>
            <div className="self-stretch bg-white flex flex-col justify-start items-start gap-3.5">
              <div className="w-full max-w-[800px] flex flex-col justify-start items-start gap-[20px] md:gap-5">
                <div className="flex-col md:flex-row self-stretch inline-flex justify-start items-start gap-[7px] md:gap-5">
                  <div className="w-44 md:py-3 flex justify-start items-start gap-2.5">
                    <div className="justify-start text-black text-base font-medium font-['Noto_Sans_JP'] leading-relaxed">
                      商品名
                    </div>
                  </div>
                  <div className="w-full flex-1 inline-flex flex-col justify-center items-start gap-[5px]">
                    <Controller
                      control={control}
                      name="product_name"
                      render={({ field }) => (
                        <Input
                          required
                          showCount
                          maxLength={40}
                          {...field}
                          placeholder="例：廃プラスチック、金属くず、木くず etc."
                          className="max-w-[604px] w-full h-[50px]"
                        />
                      )}
                    />
                  </div>
                </div>
                <div className="flex-col md:flex-row self-stretch inline-flex justify-start items-start md:items-center gap-[7px] md:gap-5">
                  <div className="w-44 flex justify-start items-center gap-2.5">
                    <div className="justify-start text-black text-base font-medium font-['Noto_Sans_JP'] leading-relaxed">
                      カテゴリー
                    </div>
                  </div>
                  <Controller
                    control={control}
                    name="category_id"
                    rules={{ required: true }}
                    render={({ field }) => (
                      <Select
                        {...field}
                        allowClear
                        options={Object.values(PRODUCT_CATEGORY)}
                        placeholder="カテゴリーを選択してください"
                        className="max-w-[604px] w-full h-[50px]"
                      />
                    )}
                  />
                </div>
                <div className="flex-col md:flex-row self-stretch inline-flex justify-start items-start md:items-center gap-[7px] md:gap-5">
                  <div className="w-44 flex justify-start items-start gap-2.5">
                    <div className="justify-start text-black text-base font-medium font-['Noto_Sans_JP'] leading-relaxed">
                      数量（Kg）
                    </div>
                  </div>
                  <div className="max-w-[604px] w-full flex flex-col md:flex-row gap-[10px]">
                    <Controller
                      control={control}
                      name="quantity_value"
                      rules={{ required: true }}
                      render={({ field }) => (
                        <InputNumber
                          required
                          {...field}
                          min={0}
                          placeholder="例：1.3 Kg"
                          className="w-full md:w-[200px] [&_.ant-input-number-input]:h-[50px]"
                        />
                      )}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="self-stretch flex flex-col justify-start items-start gap-[20px] md:gap-7">
            <div
              data-プロパティ1="base"
              className="self-stretch py-2.5 border-b border-neutral-border inline-flex justify-start items-center gap-3.5"
            >
              <div className="w-1 self-stretch bg-primary rounded-sm" />
              <div className="flex-1 flex justify-start items-center gap-2.5">
                <div className="flex justify-start items-center gap-[5px]">
                  <div className="justify-start text-primary text-[18px] md:text-[22px] font-bold font-['Noto_Sans_JP'] leading-9">
                    出品詳細
                  </div>
                </div>
              </div>
            </div>
            <div className="self-stretch bg-white flex flex-col justify-start items-start gap-3.5">
              <div className="w-full max-w-[800px] flex flex-col justify-start items-start gap-5">
                <div className="flex-col md:flex-row self-stretch min-h-12 inline-flex justify-start items-start md:items-center gap-5">
                  <div className="w-44 flex justify-start items-start gap-2.5">
                    <div className="justify-start text-black text-base font-medium font-['Noto_Sans_JP'] leading-relaxed">
                      商品名
                    </div>
                  </div>
                  <div className="flex-1 inline-flex flex-col justify-center items-start gap-[5px]">
                    <div className="self-stretch flex flex-col md:flex-row md:inline-flex justify-start items-start gap-[15px] md:gap-7 flex-wrap content-start disposal-type">
                      <Controller
                        control={control}
                        name="disposal_type"
                        render={({ field }) => (
                          <Radio.Group
                            {...field}
                            options={Object.values(DISPOSAL_TYPE)}
                            defaultValue="0"
                          />
                        )}
                      />
                    </div>
                  </div>
                </div>
                <div className="self-stretch flex-col md:flex-row md:inline-flex justify-start items-center gap-[7px] md:gap-5">
                  <div className="w-44 flex justify-start items-center gap-2.5">
                    <div className="justify-start text-black text-base font-medium font-['Noto_Sans_JP'] leading-relaxed">
                      商品の状態
                    </div>
                  </div>
                  <Controller
                    control={control}
                    name="state"
                    rules={{ required: true }}
                    render={({ field }) => (
                      <Select
                        {...field}
                        allowClear
                        options={Object.values(PRODUCT_STATE)}
                        placeholder="状態を選択する"
                        className="max-w-[604px] w-full h-[50px]"
                      />
                    )}
                  />
                </div>
                <div className="self-stretch flex-col md:flex-row md:inline-flex justify-start items-start gap-5">
                  <div className="w-44 py-3 flex justify-start items-start gap-2.5">
                    <div className="justify-start text-black text-base font-medium font-['Noto_Sans_JP'] leading-relaxed">
                      商品の説明
                    </div>
                  </div>
                  <Controller
                    control={control}
                    name="description"
                    render={({ field }) => (
                      <TextArea
                        required
                        {...field}
                        rows={6}
                        placeholder="例：臭気があります／形状にばらつきあり etc."
                        className="w-[604px]"
                      />
                    )}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="self-stretch flex flex-col justify-start items-start gap-[20px] md:gap-7">
            <div
              data-プロパティ1="base"
              className="self-stretch py-2.5 border-b border-neutral-border inline-flex justify-start items-center gap-3.5"
            >
              <div className="w-1 self-stretch bg-primary rounded-sm" />
              <div className="flex-1 flex justify-start items-center gap-2.5">
                <div className="flex justify-start items-center gap-[5px]">
                  <div className="justify-start text-primary text-[18px] md:text-[22px] font-bold font-['Noto_Sans_JP'] leading-9">
                    配送について
                  </div>
                </div>
              </div>
            </div>
            <div className="self-stretch bg-white flex flex-col justify-start items-start gap-3.5">
              <div className="w-full max-w-[800px] flex flex-col justify-start items-start gap-5">
                <div className="self-stretch flex-col md:flex-row md:inline-flex justify-start items-center gap-5">
                  <div className="w-44 flex justify-start items-center gap-2.5">
                    <div className="justify-start text-black text-base font-medium font-['Noto_Sans_JP'] leading-relaxed">
                      配送量の負担
                    </div>
                  </div>
                  <Controller
                    control={control}
                    name="shipping_charge_type"
                    rules={{ required: true }}
                    render={({ field }) => (
                      <Select
                        {...field}
                        allowClear
                        options={Object.values(SHIP_CHARGE_TYPE)}
                        placeholder="選択してください"
                        className="max-w-[604px] w-full h-[50px]"
                      />
                    )}
                  />
                </div>
                <div className="self-stretch flex-col md:flex-row md:inline-flex justify-start items-center gap-5">
                  <div className="w-44 flex justify-start items-center gap-2.5">
                    <div className="justify-start text-black text-base font-medium font-['Noto_Sans_JP'] leading-relaxed">
                      発送までの日数
                    </div>
                  </div>
                  <Controller
                    control={control}
                    name="shipping_days"
                    rules={{ required: true }}
                    render={({ field }) => (
                      <Select
                        {...field}
                        allowClear
                        options={Object.values(SHIP_DAY)}
                        placeholder="選択してください"
                        className="max-w-[604px] w-full h-[50px]"
                      />
                    )}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="self-stretch flex flex-col justify-start items-start gap-[20px] md:gap-7">
            <div
              data-プロパティ1="base"
              className="self-stretch py-2.5 border-b border-neutral-border inline-flex justify-start items-center gap-3.5"
            >
              <div className="w-1 self-stretch bg-primary rounded-sm" />
              <div className="flex-1 flex justify-start items-center gap-2.5">
                <div className="flex justify-start items-center gap-[5px]">
                  <div className="justify-start text-primary text-[18px] md:text-[22px] font-bold font-['Noto_Sans_JP'] leading-9">
                    販売価格
                  </div>
                </div>
              </div>
            </div>
            <div className="self-stretch bg-white flex flex-col justify-start items-start gap-3.5">
              <div className="w-full max-w-[800px] flex flex-col justify-start items-start gap-5">
                <div className="self-stretch inline-flex justify-start items-center gap-5">
                  <div className="flex-1 inline-flex flex-col justify-start items-start gap-3.5">
                    <Controller
                      control={control}
                      name="is_free"
                      render={({ field }) => (
                        <Radio.Group
                          style={style}
                          {...field}
                          options={[
                            { value: true, label: "無料で提供する" },
                            { value: false, label: "価格を設定する" },
                          ]}
                          defaultValue={false}
                        />
                      )}
                    />
                    <Controller
                      disabled={isFree}
                      control={control}
                      name="price"
                      render={({ field }) => (
                        <InputNumber
                          required
                          {...field}
                          min={0}
                          max={999000000}
                          prefix="￥"
                          parser={(value) => {
                            const numeric = value?.replace(/[^\d]/g, "") || "0";
                            return Number(numeric);
                          }}
                          onKeyPress={(e) => {
                            if (!/[0-9]/.test(e.key)) {
                              e.preventDefault();
                            }
                          }}
                          className="max-w-[604px] w-full h-[50px]"
                        />
                      )}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="self-stretch flex flex-col justify-start items-start gap-3.5">
          <div className="self-stretch flex flex-col-reverse md:flex-row md:inline-flex justify-start items-start gap-3.5">
            <Button
              block
              type="primary"
              htmlType="button"
              className="max-w-[340px] min-h-[60px] bg-primary-light text-primary text-lg font-bold"
              onClick={onDraft}
            >
              下書きに保存する
            </Button>
            <Button
              block
              type="primary"
              htmlType="submit"
              className="max-w-[340px] min-h-[60px] bg-primary text-lg font-bold"
            >
              出品内容を確認する
            </Button>
          </div>
        </div>
      </div>
    </form>
  );
};

export default CreateProduct;
